import {
  addDays, endOfMonth, format, startOfMonth, subDays, subMonths
} from "date-fns";

/**
 * Filters of the reservations list (`/ticket/reservations`). Every filter lives in the URL under
 * the key below, so a filtered list survives a reload and can be shared as a link, and every
 * change sends the list back to page 1. `ReservationFilterBar` writes them, `Reservations` reads
 * them into the `reservation/journeysV2` request.
 */
export type FilterOption = { label: string; value: string };

export const RESERVATION_FILTER_KEYS = {
  search: "searchkeyword",
  // "travel" selects dateFrom/dateTo by travel date; absent means purchase date.
  dateType: "dateType",
  dateFrom: "dateFrom",
  dateTo: "dateTo",
  // Name kept beside the id only to label the picker after a reload; the API gets the id.
  operatorId: "operatorId",
  operatorName: "operatorName",
  lineId: "lineId",
  lineName: "lineName",
  paymentMethodId: "paymentMethodId",
  statusId: "statusId",
  addOns: "addOns"
};

export const RESERVATION_STATUS_OPTIONS: FilterOption[] = [
  { label: "I konfirmuar", value: "1" },
  { label: "I pa përfunduar", value: "2" },
  { label: "I anuluar", value: "3" },
  { label: "I rimbursuar", value: "4" }
];

/**
 * Values are PaymentMethodEnum on the backend (Core/Models/Enums/PaymentMethods.cs). Only the two
 * online methods in use are offered; the backend accepts any of the enum's values.
 */
export const RESERVATION_PAYMENT_METHOD_OPTIONS: FilterOption[] = [
  { label: "Stripe", value: "2" },
  { label: "NLB", value: "7" }
];

/**
 * Add-on values are the API contract — they are sent verbatim as one comma-separated `addOns`
 * query param and must stay in sync with the backend's
 * Core/Constants/ReservationAddOnFilterConstants.cs.
 *
 * Selections are AND-ed server-side, and a reservation has exactly one FlexiRide type, so two
 * flex tiers together can only ever return nothing. The bar therefore offers the tier as a
 * single choice and the other add-ons as checkboxes; both still share the one `addOns` param.
 */
export const RESERVATION_FLEX_OPTIONS: FilterOption[] = [
  { label: "Ultra Flex", value: "ultraflex" },
  { label: "Premium Flex", value: "premiumflex" },
  { label: "Basic Flex", value: "basicflex" }
];

export const RESERVATION_EXTRA_OPTIONS: FilterOption[] = [
  { label: "SMS", value: "sms" },
  { label: "Valixhe", value: "luggage" },
  { label: "Open Return", value: "openreturn" }
];

export const URL_DATE = "yyyy-MM-dd";

export type DatePreset = { label: string; from: string; to: string };

// Quick ranges of the date filter. "Nesër" only makes sense for a travel date.
export const getDatePresets = (travel: boolean): DatePreset[] => {
  const today = new Date();
  const day = (d: Date) => format(d, URL_DATE);
  const lastMonth = subMonths(today, 1);
  return [
    { label: "Sot", from: day(today), to: day(today) },
    ...(travel ? [{ label: "Nesër", from: day(addDays(today, 1)), to: day(addDays(today, 1)) }] : []),
    { label: "Dje", from: day(subDays(today, 1)), to: day(subDays(today, 1)) },
    { label: "7 ditët e fundit", from: day(subDays(today, 6)), to: day(today) },
    { label: "30 ditët e fundit", from: day(subDays(today, 29)), to: day(today) },
    { label: "Ky muaj", from: day(startOfMonth(today)), to: day(today) },
    { label: "Muaji i kaluar", from: day(startOfMonth(lastMonth)), to: day(endOfMonth(lastMonth)) }
  ];
};

// Chip text for a multi-value filter: at most two labels, then "+N".
export const summarise = (labels: string[]) =>
  labels.length > 2 ? `${labels.slice(0, 2).join(", ")} +${labels.length - 2}` : labels.join(", ");
